import React from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Users, CalendarDays, Phone, Mail } from "lucide-react";
import EventCard from "@/components/common/EventCard";
import { Spinner, EmptyState, Badge } from "@/components/common/Primitives";
import { formatDate } from "@/lib/format";
import { isInitialLoading, useUnit, useUnitPosts, useUnitEvents } from "@/hooks/useSiteQueries";

export default function UnitDetailPage() {
  const { slug } = useParams();
  const unitQuery = useUnit(slug);
  const postsQuery = useUnitPosts(slug);
  const eventsQuery = useUnitEvents(slug);

  if (isInitialLoading([unitQuery, postsQuery, eventsQuery])) return <Spinner className="py-32" />;

  const unit = unitQuery.data || null;
  const posts = postsQuery.data;
  const events = eventsQuery.data;

  if (!unit)
    return (
      <div className="container-bcs py-24">
        <EmptyState title="Unit not found" message="The unit you are looking for does not exist." />
      </div>
    );

  return (
    <div data-testid={`unit-detail-${slug}`}>
      <section className="relative bg-maroon text-white overflow-hidden">
        <img src={unit.image_url} alt={unit.parish_name} className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-maroon via-maroon/90 to-maroon/60" />
        <div className="relative container-bcs py-20 sm:py-28">
          <Link to="/units" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors mb-6">
            <ArrowLeft size={15} /> All units
          </Link>
          <Badge tone="gold">{unit.deanery} Deanery</Badge>
          <h1 className="mt-4 heading-xl !text-white max-w-3xl">{unit.name}</h1>
          <div className="mt-5 flex flex-wrap gap-x-8 gap-y-2 text-sm text-white/85">
            <span className="flex items-center gap-2"><MapPin size={15} className="text-gold-light" /> {unit.area}, Mumbai</span>
            {unit.member_count && (
              <span className="flex items-center gap-2">
                <Users size={15} className="text-gold-light" /> {unit.member_count.toLocaleString("en-IN")} members
              </span>
            )}
            {unit.established_year && (
              <span className="flex items-center gap-2">
                <CalendarDays size={15} className="text-gold-light" /> Unit since {unit.established_year}
              </span>
            )}
          </div>
        </div>
      </section>

      <section className="container-bcs py-14 sm:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-12">
            <div>
              <h2 className="heading-md mb-4">About the unit</h2>
              <p className="text-ink-soft leading-relaxed">{unit.description}</p>
            </div>

            <div>
              <h2 className="heading-md mb-5">Unit updates</h2>
              {posts.length === 0 ? (
                <EmptyState title="No updates yet" message="This unit hasn't posted any updates." />
              ) : (
                <div className="space-y-6">
                  {posts.map((p) => (
                    <article key={p.id} className="card-bcs overflow-hidden sm:flex" data-testid={`unit-post-${p.id}`}>
                      {p.image_url && <img src={p.image_url} alt={p.title} className="sm:w-56 h-44 sm:h-auto w-full object-cover" loading="lazy" />}
                      <div className="p-6">
                        <p className="text-xs text-ink-muted">{formatDate(p.created_at)}</p>
                        <h3 className="mt-1.5 font-serif text-lg font-semibold text-ink">{p.title}</h3>
                        <p className="mt-2 text-sm text-ink-soft leading-relaxed">{p.body}</p>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </div>

            <div>
              <h2 className="heading-md mb-5">Unit events</h2>
              {events.length === 0 ? (
                <EmptyState title="No events listed" message="No events are currently associated with this unit." />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {events.map((e) => (
                    <EventCard key={e.slug} event={e} />
                  ))}
                </div>
              )}
            </div>
          </div>

          <aside className="space-y-5">
            <div className="card-bcs p-6">
              <p className="eyebrow !text-[10px]">Unit details</p>
              <dl className="mt-4 space-y-3 text-sm">
                <div>
                  <dt className="text-ink-muted">Parish</dt>
                  <dd className="font-medium text-ink mt-0.5">{unit.parish_name}</dd>
                </div>
                <div>
                  <dt className="text-ink-muted">Address</dt>
                  <dd className="font-medium text-ink mt-0.5">{unit.address}</dd>
                </div>
                {unit.president && (
                  <div>
                    <dt className="text-ink-muted">Unit President</dt>
                    <dd className="font-medium text-ink mt-0.5">{unit.president}</dd>
                  </div>
                )}
              </dl>
              {(unit.contact_phone || unit.contact_email) && (
                <div className="mt-5 pt-4 border-t border-gray-100 space-y-2.5">
                  {unit.contact_phone && (
                    <a href={`tel:${unit.contact_phone}`} className="flex items-center gap-2.5 text-sm text-maroon font-medium" data-testid="unit-phone">
                      <Phone size={15} /> {unit.contact_phone}
                    </a>
                  )}
                  {unit.contact_email && (
                    <a href={`mailto:${unit.contact_email}`} className="flex items-center gap-2.5 text-sm text-maroon font-medium" data-testid="unit-email">
                      <Mail size={15} /> {unit.contact_email}
                    </a>
                  )}
                </div>
              )}
            </div>
            <div className="card-bcs p-6 bg-cream-alt">
              <p className="font-serif text-lg font-semibold text-ink">Want to join this unit?</p>
              <p className="mt-1.5 text-sm text-ink-soft">Send us a membership enquiry and the unit will reach out to you.</p>
              <Link to="/contact" className="btn-primary mt-4 w-full" data-testid="unit-join-btn">Membership enquiry</Link>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
